import React, { useContext, useEffect, useRef, useState } from "react";
import AnimatedNumber from "react-animated-number";
import {
  DiscountBannerIcon,
  DiscountBannerText,
  DiscountBannerTitle,
  DiscountBannerWrapper
} from "@widgets/discountBanner/styles";
import { Context } from "@components/useContext/context";
import { resolvePath } from "@utils/resolvePath";
import { getInrFormat } from "@utils/inrFormatter";
import { GOOD_NEWS_COUNTER_PHRASE } from "@utils/premiumSavingsBannerConfig";

/** Pause after “Good news” is visible, then run counter (loyalty stacked on) */
export const COUNTER_START_DELAY_MS = 950;

const COUNTER_DURATION_MS = 1600;

type Props = {
  fromAmount: number;
  toAmount: number;
  icon?: unknown;
  iconAlt?: string;
  styles?: React.CSSProperties;
  /** Fires once when counter reaches final value */
  onCounterComplete?: () => void;
};

/**
 * “Good news! You save ₹486…” then after delay counts up to total (e.g. ₹986).
 */
const GoodNewsSavingsCounterBanner = ({
  fromAmount,
  toAmount,
  icon,
  iconAlt,
  styles,
  onCounterComplete
}: Props) => {
  const { isDesktop } = useContext(Context);
  const [counterRunning, setCounterRunning] = useState(false);
  const completedRef = useRef(false);

  useEffect(() => {
    completedRef.current = false;
  }, [fromAmount, toAmount]);

  useEffect(() => {
    if (fromAmount >= toAmount) {
      const t = window.setTimeout(() => {
        if (!completedRef.current) {
          completedRef.current = true;
          onCounterComplete?.();
        }
      }, COUNTER_START_DELAY_MS);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => setCounterRunning(true), COUNTER_START_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [fromAmount, toAmount, onCounterComplete]);

  useEffect(() => {
    if (!counterRunning || fromAmount >= toAmount) return undefined;
    const t = window.setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onCounterComplete?.();
      }
    }, COUNTER_DURATION_MS + 120);
    return () => window.clearTimeout(t);
  }, [counterRunning, fromAmount, toAmount, onCounterComplete]);

  const layoutStyles = (() => {
    const s = { ...(styles || {}) } as React.CSSProperties;
    delete s.position;
    delete s.bottom;
    delete s.left;
    delete s.right;
    delete s.zIndex;
    delete s.transform;
    return { ...s, width: "100%", boxSizing: "border-box" as const };
  })();

  const displayAmount =
    fromAmount >= toAmount ? (
      <span className="discount-green">₹{getInrFormat(toAmount)}</span>
    ) : !counterRunning ? (
      <span className="discount-green">₹{getInrFormat(fromAmount)}</span>
    ) : (
      <span className="discount-green" style={{ display: "inline" }}>
        ₹
        <AnimatedNumber
          value={toAmount}
          initialValue={fromAmount}
          duration={COUNTER_DURATION_MS}
          formatValue={(n) => getInrFormat(Math.round(Number(n)))}
          frameStyle={() => ({})}
          style={{
            display: "inline",
            fontFamily: '"Euclid Circular B", sans-serif',
            fontSize: "16px",
            fontWeight: 600,
            lineHeight: "20px",
            color: "var(--Green-G400, #0b753e)"
          }}
        />
      </span>
    );

  return (
    <DiscountBannerWrapper style={layoutStyles}>
      {icon ? (
        <DiscountBannerIcon
          src={resolvePath("/images/icons/increase_insuarance_price.svg")}
          alt={iconAlt || "discount"}
        />
      ) : null}
      <DiscountBannerText isTitleOnly $isDesktop={isDesktop}>
        <DiscountBannerTitle $isDesktop={isDesktop} $hasStrikeOff={false}>
          {GOOD_NEWS_COUNTER_PHRASE.prefix}
          {displayAmount}
          {GOOD_NEWS_COUNTER_PHRASE.suffix}
        </DiscountBannerTitle>
      </DiscountBannerText>
    </DiscountBannerWrapper>
  );
};

export default GoodNewsSavingsCounterBanner;
